/**
 * # Card Expiration Input
 * An input container for payment expiration details.
 *
 * @example
 * new Spark.Payments.CardExpiration(el, {...});
 *
 * @module components/payments/card-expiration.js
 */

import BaseComponent from '../base';
import TypeAhead from '../typeahead';

import mixin from '../../helpers/util/mixin';
import addClass from '../../helpers/dom/add-class';
import removeClass from '../../helpers/dom/remove-class';
import toggleClass from '../../helpers/dom/toggle-class';
import makeElement from '../../helpers/dom/make-element';

import messaging from '../../mixins/messaging';
import validation from '../../mixins/validation';

let onValidEvent;
let onInvalidEvent;

class CardExpiration extends BaseComponent {

  /**
   * CardExpiration constructor.
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    super(el, params);

    this._bindEventListenerCallbacks();
    this._addEventListeners();
    this._initTypeahead();

    onValidEvent = document.createEvent('Event');
    onInvalidEvent = document.createEvent('Event');

    onValidEvent.initEvent('valid', true, true);
    onInvalidEvent.initEvent('invalid', true, true);
  }


  /**
   * Sets up references
   * @param {Element} el base DOM element
   */
  _cacheElements(el) {
    this.el = el;
    this.inputEl = el ? el.querySelector('input') : undefined;

    if (!this.el || !this.inputEl) {
      throw new Error('CardExpiration markup missing');
    } else {
      if (this.inputEl.value) {
        addClass(this.el, 'active');
      }

      this.messageEl = this.el.querySelector('.spark-input__message') || makeElement('<span class="spark-input__message"></span>');
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   */
  _bindEventListenerCallbacks() {
    this._onBlurBound = this._onBlur.bind(this);
    this._onFocusBound = this._onFocus.bind(this);
    this._onInputBound = this._onInput.bind(this);
    this._onInvalidBound = this._onInvalid.bind(this);
    this._onValidBound = this._onValid.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   */
  _addEventListeners() {
    this.inputEl.addEventListener('input', this._onInputBound, false);
    this.inputEl.addEventListener('blur', this._onBlurBound, false);
    this.inputEl.addEventListener('focus', this._onFocusBound, false);
    this.inputEl.addEventListener('invalid', this._onInvalidBound, false);
    this.inputEl.addEventListener('valid', this._onValidBound, false);
  }


  /**
   * Remove event listeners for DOM events..
   */
  _removeEventListeners() {
    this.inputEl.removeEventListener('input', this._onInputBound, false);
    this.inputEl.removeEventListener('blur', this._onBlurBound, false);
    this.inputEl.removeEventListener('focus', this._onFocusBound, false);
    this.inputEl.removeEventListener('invalid', this._onInvalidBound, false);
    this.inputEl.removeEventListener('valid', this._onValidBound, false);
  }


  /**
   * Initialize internal typeahead to restrict input to desired format
   */
  _initTypeahead() {
    if (this.inputEl) {
      this.inputEl.dataset.typeaheadFormat = '\\d\\d - \\d\\d';
      this.typeahead = new TypeAhead(this.el);
    }
  }


  /**
   * Getter for Month value
   * @return {String}
   */
  get month() {
    if (this._month === undefined && this.inputEl.value) {
      if (this.inputEl.value.length >= 2) {
        const month = this.inputEl.value.slice(0, 2);
        return month >= 1 && month <= 12 ? month : this._month;
      }
    }
    return this._month;
  }


  /**
   * Setter for Month Value
   * @param {Number} value
   */
  set month(value) {
    if (value && value >= 1 && value <= 12) {
      value = value.toString();
      this._month = value.length === 2 ? value : '0'.concat(value);
    } else {
      this._month = undefined;
    }

    if (this._month && this._month.length === 2) {
      switch (this.inputEl.value.length) {
        case 0:
        case 1:
          addClass(this.el, 'active');
          this.value = this._month;
          break;
        case 2:
          addClass(this.el, 'active');
          this.value = this._month;
          if (this.inputEl === document.activeElement) {
            this.typeahead.moveCaret(5);
          }
          break;
        default:
          this.value = this._month + this.inputEl.value.slice(2);
          break;
      }
    } else {
      this._month = undefined;
      this._year = undefined;
      this.typeahead.clearValue();
    }
  }


  /**
   * Getter for Year value
   * @return {String}
   */
  get year() {
    if (this._year === undefined && this.inputEl.value) {
      if (this.inputEl.value.length === 7) {
        return this.inputEl.value.substr(-2);
      }
    }
    return this._year;
  }


  /**
   * Setter for Year value
   * @param {Number} value
   */
  set year(value) {
    if (value >= 0 && value <= 99) {
      value = value.toString();

      this._year = value.length === 2 ? value : '0'.concat(value);
    } else {
      this._year = undefined;
    }

    if (this._year && this._year.length === 2) {
      switch (this.inputEl.value.length) {
        case 0:
        case 1:
          this._year = undefined;
          break;
        default:
          if (this._month) {
            this.value = this._month.concat(' - ', this._year);
          }
          break;
      }
    } else {
      this.value = this.inputEl.value.slice(0, 5);
    }
  }


  /**
   * Getter for Primary Component Value
   * @return {Object}
   */
  get value() {
    return this.valid ? {
      month: this.month,
      year: this.year
    } : undefined;
  }


  /**
   * Setter for Primary Component Value
   * @param {Mixed} val
   */
  set value(val) {
    if (!val) {
      this._month = undefined;
      this._year = undefined;

      if (this.inputEl) {
        this.inputEl.value = '';
      }

      if (this.typeahead) {
        this.typeahead.clearValue();
        this.typeahead.placeholderEl.innerText = this.inputEl.placeholder;
      }
    } else if (val.hasOwnProperty('year') || val.hasOwnProperty('month')) {
      this.month = val.month || this.month;
      this.year = val.year || this.year;
    } else if (this.typeahead && val && typeof val === 'string') {
      this.typeahead.setValue(val);
    }

    if (this.inputEl === document.activeElement) {
      addClass(this.el, 'active');
    } else {
      toggleClass(this.el, 'active', this.inputEl.value);
    }

  }


  /**
   * Getter for Valid state, returns true if component value is valid, false if not
   * @return {Boolean}
   */
  get valid() {
    const format = new RegExp(this.inputEl.getAttribute('pattern'));
    return this.month && this.year ? this.inputEl.willValidate && format.test(this.inputEl.value) : false;
  }


  /**
   * onBlur callback, for controlling component active state, firing provided callbacks, and running validation
   */
  _onBlur() {

    if (!this.inputEl.value) {
      removeClass(this.el, 'active');
    }

    this.onBlur.call(this);

    if (this.inputEl.hasAttribute('data-novalidate')) {
      this.inputEl.removeAttribute('data-novalidate');
    }

    if (!this.valid) {
      this.inputEl.dispatchEvent(onInvalidEvent);
    }
  }


  /**
   * onFocus callback, for controlling component active state and firing provided callbacks
   */
  _onFocus() {
    addClass(this.el, 'active');
    this.onFocus.call(this);
  }


  /**
   * onInput callback, for controlling component active state, parsing user input, firing provided callbacks, and running validation
   */
  _onInput() {
    const value = this.inputEl ? this.inputEl.value : 0;
    addClass(this.el, 'active');

    switch (value.length) {
      case 0:
        this.month = undefined;
        this.year = undefined;
        break;
      case 1:
        if (value > 1) {
          const formattedMonth = '0'.concat(this.inputEl.value, ' - ');
          this.inputEl.value = formattedMonth;
          this.typeahead.setValue(formattedMonth);
          this.typeahead.placeholderEl.innerHTML = '&nbsp;&nbsp; - YY';
          this.typeahead.moveCaret(5);
          this.month = value.slice(0, 2);
          this.year = undefined;
        } else {
          this.year = undefined;
        }
        break;
      case 5: {
        let month = value.slice(0, 2);
        if (month >= 1 && month <= 12) {
          this.month = month;
          this.typeahead.moveCaret(5);
        } else {
          this.typeahead.setValue(month.charAt(0));
          this.typeahead.moveCaret(1);
          this.inputEl.value = month.charAt(0);
          this.typeahead.placeholderEl.innerHTML = '&nbsp;' + this.inputEl.placeholder.slice(1);
        }
        this.year = undefined;
        break;
      }
      case 6:
        this.month = value.slice(0, 2);
        break;
      case 7:
        this.month = value.slice(0, 2);
        this.year = value.substr(-2);
        break;
    }

    this.onInput.call(this);

    this._checkValidity();
  }


  /**
   * Triggers validity events
   */
  _checkValidity() {
    if (!this.inputEl.hasAttribute('data-novalidate')) {
      this.inputEl.dispatchEvent(this.valid ? onValidEvent : onInvalidEvent);
    }
  }


  /**
   * onInvalid method for firing provided callbacks
   */
  _onInvalid() {
    this.onInvalid.call(this);
  }


  /**
   * onValid method for firing provided callbacks
   */
  _onValid() {
    this.onValid.call(this);
  }


  /**
   * Method for triggering focus on component's input element from outside of component
   */
  focus() {
    this.inputEl.focus();
  }


  /**
   * Returns value as a string of continuous digits in the pattern of MMYY,
   * with no special formatting or spacing
   * @return {String}
   */
  valueOf() {
    return this.inputEl.value.replace(/\D/g, '');
  }


  /**
   * Default validate function. This can be overridden by passing a
   * custom validate method as a parameter.
   */
  validate() {
    if (this.validatePattern) {
      this.inputEl.setAttribute('pattern', this.validatePattern);
    }

    this.onValidate(this.valid, this.value, this);

    return this;
  }


  /**
   * Show the input by adding the active state and setting character counts (if necessary).
   */
  show() {
    addClass(this.el, 'active');
    return this;
  }


  /**
   * Hide the input by removing the active state.
   */
  hide() {
    removeClass(this.el, 'active');
    return this;
  }


  /**
   * Set the value of the input.
   * @param {Mixed} value
   */
  setValue(value) {
    this.value = value;

    if (value) {
      this.show();
    } else {
      this.hide();
    }

    return this;
  }


  /**
   * Get the value of the input.
   * @return {String}
   */
  getValue() {
    return this.value;
  }


  /**
   * Clear the value of the input.
   */
  clearValue() {
    this.value = undefined;
    return this;
  }


  /**
   * Disable the input.
   */
  disable() {
    this.inputEl.attributes.setNamedItem(document.createAttribute('disabled'));
    return this;
  }


  /**
   * Enable the input.
   */
  enable() {
    this.inputEl.removeAttribute('disabled');
    return this;
  }


  /**
   * Remove typeahead.
   * @param {Boolean} leaveElement Leave the element intact.
   */
  _removeTypeahead(leaveElement) {
    if (this.typeahead) {
      this.typeahead.remove(leaveElement);
    }
  }


  /**
   * Remove component from DOM.
   * @param {Boolean} leaveElement
   */
  remove(leaveElement) {
    this._removeTypeahead(leaveElement);
    return super.remove(leaveElement);
  }


  /**
   * Update the element in use and the position.
   * @param {Element} el
   */
  update(el) {
    this._removeTypeahead(true);
    super.update(el);
    this._initTypeahead();

    return this;
  }
}

/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
CardExpiration.prototype._whitelistedParams = [
  'onInput',
  'onBlur',
  'onFocus',
  'onInvalid',
  'onValid',
  'onValidate',
  'validate',
  'validatePattern'
];

/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
CardExpiration.prototype.defaults = {
  el: null,
  inputEl: null,
  validate: null,
  validatePattern: null,
  onInput: () => {},
  onBlur: () => {},
  onFocus: () => {},
  onInvalid: () => {},
  onValid: () => {},
  onValidate: () => {}
};

mixin(CardExpiration.prototype, messaging, validation);

export default CardExpiration;
